import { AsyncPipe, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import {
  ButtonTextDirective,
  FormCheckboxComponent,
  FormCheckboxDirective,
  FormLabelDirective,
} from '@app/base-components';
import { ButtonQaId, HabaQaId } from '@app/helpers';
import { HabaActiveCartService, LoaderService, PlaceOrderService } from '@app/services';
import { I18nModule, ORDER_TYPE, RoutingService } from 'haba-core';
import { EmporixPaymentService, EmporixStockService } from 'haba-emporix';
import {
  CheckoutReplenishmentFormService,
  PlaceOrderComponent,
} from 'haba-featurelibs/checkout/components';
import { CheckoutFacade } from 'haba-featurelibs/checkout/root';
import { LaunchDialogService } from 'haba-storefrontlib';
import { Observable, Subject, Subscription, throwError } from 'rxjs';
import { catchError, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-place-order',
  templateUrl: './place-order.component.html',
  styleUrls: ['./place-order.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    NgIf,
    FormCheckboxComponent,
    FormLabelDirective,
    FormCheckboxDirective,
    ButtonTextDirective,
    I18nModule,
    AsyncPipe,
  ],
})
export class HabaPlaceOrderComponent extends PlaceOrderComponent implements OnDestroy, OnInit {
  @Input() showTermsAndConditions = true;

  baseId: HabaQaId;
  submitButtonId: ButtonQaId;

  preventSubmit = false;
  disableSendButton: boolean = true;
  disableSubmitTimeout = 6000;
  public requestInProgress = false;
  private destroyed$ = new Subject();

  private readonly subscriptions: Subscription[] = [];
  hasOnlyOutOfStockProducts$: Observable<boolean> =
    this.activeCartService.hasOnlyOutOfStockProducts();

  constructor(
    protected cdr: ChangeDetectorRef,
    protected checkoutService: CheckoutFacade,
    protected routingService: RoutingService,
    protected fb: FormBuilder,
    protected checkoutReplenishmentFormService: CheckoutReplenishmentFormService,
    protected launchDialogService: LaunchDialogService,
    protected vcr: ViewContainerRef,
    protected placeOrderService: PlaceOrderService,
    protected emporixPaymentService: EmporixPaymentService,
    private loaderSevice: LoaderService,
    protected emporixStockService: EmporixStockService,
    protected activeCartService: HabaActiveCartService
  ) {
    super(
      checkoutService,
      routingService,
      fb,
      checkoutReplenishmentFormService,
      launchDialogService,
      vcr
    );
  }

  toggleFormAction() {
    this.preventSubmit = true;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.preventSubmit = false;
      this.cdr.detectChanges();
    }, this.disableSubmitTimeout);
  }

  submitForm(): void {
    if (this.preventSubmit) return;
    this.toggleFormAction();
    if (this.checkoutSubmitForm.invalid || this.requestInProgress) {
      this.checkoutSubmitForm.markAllAsTouched();
      return;
    }
    this.loaderSevice.show();
    this.requestInProgress = true;

    this.placeOrderService
      .startPayment(false)
      .pipe(
        takeUntil(this.destroyed$),
        take(1),
        tap((paymentData) => {
          this.requestInProgress = false;
          this.emporixPaymentService.handleNextStep(paymentData);
        }),
        catchError((error) => {
          this.loaderSevice.hide();
          this.requestInProgress = false;
          this.cdr.detectChanges();
          return throwError(() => error);
        })
      )
      .subscribe(() => {
        this.loaderSevice.hide();
        this.requestInProgress = false;
        this.cdr.detectChanges();
      });
  }

  ngOnInit() {
    this.initIds();
    super.ngOnInit();

    if (!this.showTermsAndConditions) {
      this.checkoutSubmitForm.controls.termsAndConditions.setValue(true);
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private initIds() {
    this.baseId = HabaQaId.forPage(HabaQaId.page.reviewOrder);
    this.submitButtonId = this.baseId.getButtonId(HabaQaId.elements.button.submit);
  }

  onSuccess(data: boolean): void {
    if (data) {
      this.checkoutService
        .getOrderDetails()
        .subscribe((order) => {
          switch (this.currentOrderType) {
            case ORDER_TYPE.PLACE_ORDER: {
              if (order && order.code) {
                this.routingService.go(
                  { cxRoute: 'orderConfirmation' },
                  { queryParams: { orderId: order.code } }
                );
              }
              break;
            }
            case ORDER_TYPE.SCHEDULE_REPLENISHMENT_ORDER: {
              this.routingService.go({ cxRoute: 'replenishmentConfirmation' });
              break;
            }
          }
          this.checkoutReplenishmentFormService.resetScheduleReplenishmentFormData();
        })
        .unsubscribe();
    }
  }
}
